/**
 * The employee's own conversation history.
 *
 * "Own" is the load-bearing word and it is enforced on the server: `/sessions` is scoped to the
 * caller in the query, and asking for another employee's session returns 404. Nothing here can
 * widen that, which is the point — a history screen that could is a screen that eventually does.
 *
 * The list shows the outcome of each conversation, not just its title. An employee looking for
 * "the one where it couldn't find the answer" is looking for an outcome, and a list of question
 * fragments makes them open conversations one by one to find it.
 *
 * History is a convenience view, not the record: it is retained for 90 days while the audit store
 * keeps the turn for years. A gap here is a missing list entry, never a missing fact.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ApiError,
  Card,
  EmptyState,
  cx,
  directionOfText,
  fetchSessionMessages,
  fetchSessions,
  type SessionMessage,
  type SessionSummary,
  formatTimestamp,
} from "@nab/shared";

export interface HistoryPageProps {
  employeeId?: string;
}

export function HistoryPage({ employeeId }: HistoryPageProps): JSX.Element {
  const { t } = useTranslation("app");
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [messages, setMessages] = useState<SessionMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const options = useCallback(
    () => (employeeId ? { employeeId } : {}),
    [employeeId],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchSessions(options())
      .then((result) => {
        if (!cancelled) setSessions(result);
      })
      .catch((caught) => {
        if (!cancelled) setError(caught instanceof ApiError ? t("history.failed") : String(caught));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [options, t]);

  async function open(sessionId: string) {
    setSelected(sessionId);
    setMessages([]);
    try {
      setMessages(await fetchSessionMessages(sessionId, options()));
    } catch {
      setError(t("history.failed"));
    }
  }

  return (
    <section className="flex flex-col gap-6" aria-labelledby="history-heading">
      <Card>
        <h2 id="history-heading" className="text-heading-md text-textPrimary">
          {t("history.heading")}
        </h2>
        <p className="mt-1 max-w-prose text-body-sm text-textMuted">{t("history.description")}</p>
      </Card>

      {error ? (
        <Card className="border-statusDanger bg-statusDangerSubtle" role="alert">
          <p className="text-body-md text-textPrimary">{error}</p>
        </Card>
      ) : null}

      {/* The list is the narrow rail and the transcript is the reading column, in the proportion
          the chat screen uses. Below the tablet breakpoint they stack rather than shrink: a
          two-line session summary in a third of a narrow screen is unreadable. */}
      <div className="grid gap-6 md:grid-cols-3">
        <nav aria-label={t("history.listLabel")} className="md:col-span-1">
          <Card>
            {loading ? (
              <p role="status" aria-live="polite" className="text-body-sm text-textMuted">
                {t("history.loading")}
              </p>
            ) : null}
            {!loading && sessions.length === 0 ? (
              <EmptyState title={t("history.empty")} description={t("history.emptyNextStep")} />
            ) : null}
            <ul className="flex flex-col gap-1">
              {sessions.map((session) => (
                <li key={session.session_id}>
                  <button
                    type="button"
                    className={cx(
                      "flex w-full flex-col gap-1 rounded-sm px-3 py-2 text-start",
                      "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
                      session.session_id === selected
                        ? "bg-surfaceSunken text-textPrimary"
                        : "text-textMuted hover:bg-surfaceSunken",
                    )}
                    aria-current={session.session_id === selected ? "true" : undefined}
                    onClick={() => void open(session.session_id)}
                  >
                    {/* Clamped rather than truncated to one line: a session title is a fragment of
                        the question asked, and one line of it is often not enough to recognise. */}
                    <span
                      className="line-clamp-2 text-body-md text-textPrimary"
                      dir={directionOfText(session.title ?? "")}
                    >
                      <bdi>{session.title ?? t("history.untitled")}</bdi>
                    </span>
                    <span className="text-label-sm text-textMuted">
                      {t("history.turnCount", { count: session.turn_count })}
                      {" · "}
                      {/* `dir="ltr"` around a format that carries no direction marks of its own —
                          see `formatTimestamp`. Both halves are needed. */}
                      <span dir="ltr">{formatTimestamp(session.last_activity_at)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        </nav>

        {/* No card around the transcript: in chat the conversation sits directly on the page and
            the message treatments are drawn against that surface. Boxing it here would put a
            raised bubble on a raised panel, where its border is the only thing left of it. */}
        <div className="flex flex-col gap-6 md:col-span-2">
          {selected === null ? (
            <EmptyState title={t("history.selectPrompt")} />
          ) : (
            messages.map((message) => (
              <Transcribed key={message.message_id} message={message} />
            ))
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * One turn, in the shape the chat screen gives it: the employee's question as a bounded bubble on
 * the inline end, the answer as full-width prose. Deliberately the same treatments as
 * `components/Message` — an employee rereading a conversation should recognise the answer they
 * were given, and a second visual language for the same content invites doubt about whether it is
 * the same content.
 *
 * The timestamp is the one thing history adds. It is what an employee is scanning for when they
 * come back to a conversation, and it is the field the list rail sorts on.
 */
function Transcribed({ message }: { message: SessionMessage }): JSX.Element {
  const { t } = useTranslation("app");

  if (message.role === "user") {
    return (
      <article className="flex justify-end">
        <div className="max-w-prose rounded-md border border-borderSubtle bg-surfaceRaised px-4 py-3">
          <div
            className="whitespace-pre-wrap text-start text-body-md text-textPrimary"
            dir={directionOfText(message.content)}
          >
            {message.content}
          </div>
          {/* `dir="ltr"` around a format that carries no direction marks of its own — see
              `formatTimestamp`. Both halves are needed. */}
          <p className="mt-2 text-label-sm text-textMuted" dir="ltr">
            {formatTimestamp(message.created_at)}
          </p>
        </div>
      </article>
    );
  }

  const answered = message.outcome === "answered" || message.outcome === null;

  return (
    <article className={cx("flex max-w-prose flex-col gap-3", outcomeClass(message.outcome))}>
      <div
        className={cx(
          "whitespace-pre-wrap text-start",
          // The answer is the thing being read, so it is set at reading size; a refusal is a short
          // explanation and sits at the weight of one. Same rule as the live transcript.
          answered ? "text-body-lg text-textPrimary" : "text-body-md text-textMuted",
        )}
        dir={directionOfText(message.content)}
      >
        {message.content}
      </div>
      {message.citations.length > 0 ? (
        <p className="text-label-sm text-textMuted">
          {t("history.sourceCount", { count: message.citations.length })}
        </p>
      ) : null}
    </article>
  );
}

/** The accent edge for "not in the knowledge base" — look here, nothing is wrong — and the inert
 *  dashed shell for a guardrail refusal. Both are lifted from `components/Message` rather than
 *  invented, so the same outcome does not get two appearances in one product. */
function outcomeClass(outcome: SessionMessage["outcome"]): string {
  switch (outcome) {
    case "no_grounding":
      return "rounded-md border border-borderSubtle border-s-4 border-s-accent bg-surfaceRaised p-4";
    case "refused":
    case "withheld":
      return "rounded-md border border-dashed border-borderStrong bg-surfaceSunken p-4";
    default:
      return "";
  }
}
